import { Controller } from "@hotwired/stimulus"

// Highlights the settings section being read, for a settings page made of several sections
// (a project's Settings page, the organization's General settings with its Status page and
// Danger zone), the same way the docs sidebar tracks its topics (see docs_toc_controller.js).
// Only links to a section on this same page are tracked; a link to another page keeps the
// highlight the server gave it. A same-page link with no #section is the top of the page: it's
// the current one until another section scrolls up past the line below the navbar.
export default class extends Controller {
  static targets = ["link"]

  connect() {
    // Sticks just below whatever sticky header this page has: the navbar alone on the
    // organization's settings, the navbar plus the project sub-nav on a project's Settings page.
    // Measured rather than hard-coded, since the two differ (and the sub-nav can wrap).
    this.placeBelowHeader = this.placeBelowHeader.bind(this)
    this.placeBelowHeader()
    window.addEventListener("resize", this.placeBelowHeader, { passive: true })

    this.tracked = this.linkTargets
      .map(link => {
        const url = new URL(link.href, window.location.href)
        if (url.pathname !== window.location.pathname) return null
        const section = url.hash ? document.getElementById(url.hash.slice(1)) : null
        if (url.hash && !section) return null
        return { link, section }
      })
      .filter(Boolean)
    if (this.tracked.length === 0) return

    this.onScroll = this.onScroll.bind(this)
    this.onUserScroll = this.onUserScroll.bind(this)
    this.onClick = this.onClick.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    for (const event of [ "wheel", "touchmove", "keydown" ]) window.addEventListener(event, this.onUserScroll, { passive: true })
    this.element.addEventListener("click", this.onClick)

    // Opened at a #section (a link from elsewhere, or a reload after clicking one): that section.
    const opened = this.tracked.find(entry => entry.section && `#${entry.section.id}` === window.location.hash)
    if (opened) this.choose(opened)
    else this.onScroll()
  }

  placeBelowHeader() {
    const header = document.querySelector(".sticky-top")
    const offset = Math.ceil((header ? header.getBoundingClientRect().height : 56) + 16)
    this.element.style.setProperty("--settings-nav-top", `${offset}px`)
    // Jumping to a #section lands it just below the header instead of underneath it. Set on
    // <html> only while a settings page is open: Turbo keeps <html> across page visits.
    document.documentElement.style.scrollPaddingTop = `${offset}px`
  }

  disconnect() {
    window.removeEventListener("resize", this.placeBelowHeader)
    document.documentElement.style.scrollPaddingTop = ""
    if (!this.onScroll) return
    window.removeEventListener("scroll", this.onScroll)
    for (const event of [ "wheel", "touchmove", "keydown" ]) window.removeEventListener(event, this.onUserScroll)
    this.element.removeEventListener("click", this.onClick)
  }

  // A section chosen from the list stays highlighted even when it's too short, or too near the
  // bottom, for scrolling to ever reach it, until the reader scrolls on their own again.
  onClick(event) {
    const entry = this.tracked.find(tracked => tracked.link === event.target.closest("a"))
    if (entry) this.choose(entry)
  }

  choose(entry) {
    this.chosen = true
    this.activate(entry)
  }

  onUserScroll() {
    this.chosen = false
  }

  onScroll() {
    if (this.chosen) return
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      const line = (parseInt(this.element.style.getPropertyValue("--settings-nav-top")) || 72) + 48
      let current = null
      let currentTop = -Infinity
      for (const entry of this.tracked) {
        if (!entry.section) continue
        const top = entry.section.getBoundingClientRect().top
        if (top <= line && top > currentTop) {
          current = entry
          currentTop = top
        }
      }
      // Scrolled to the very bottom: the last section wins even when it's too short to reach the
      // line, otherwise a short Danger zone at the end could never be highlighted.
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
      if (atBottom) {
        const last = [ ...this.tracked ].reverse().find(entry => entry.section)
        if (last) current = last
      }
      current ||= this.tracked.find(entry => !entry.section) || this.tracked[0]
      this.activate(current)
    })
  }

  // The list is rendered twice (a <details> for phones and the sidebar from lg up), so every
  // link to the same section lights up together, not just the first one found.
  activate(entry) {
    if (entry?.link.href === this.activeHref) return
    this.activeHref = entry?.link.href
    for (const tracked of this.tracked) {
      const active = tracked.link.href === this.activeHref
      tracked.link.classList.toggle("active", active)
      if (active) {
        tracked.link.setAttribute("aria-current", "location")
      } else {
        tracked.link.removeAttribute("aria-current")
      }
    }
  }
}
